/** Customer-facing fulfilment progress (Confirm → Paid → Picked → …). */
export const ORDER_PROGRESS_STATUSES = [
  "PENDING_PAYMENT",
  "PAID",
  "PICKED",
  "PACKED",
  "SHIPPED",
  "DELIVERED",
] as const;

export type OrderProgressStatus = (typeof ORDER_PROGRESS_STATUSES)[number];

/** Staff-advanceable statuses after payment. */
export const FULFIL_ORDER_STATUSES = ["PICKED", "PACKED", "SHIPPED", "DELIVERED"] as const;

export type FulfilOrderStatus = (typeof FULFIL_ORDER_STATUSES)[number];

/** Order has cleared payment (documents, refunds gate, etc.). */
export const POST_PAYMENT_ORDER_STATUSES = ["PAID", ...FULFIL_ORDER_STATUSES] as const;

export const ORDER_STATUS_LABELS: Record<string, string> = {
  PENDING_PAYMENT: "Confirm",
  PAID: "Paid",
  PICKED: "Picked",
  PACKED: "Packed",
  SHIPPED: "Shipped",
  DELIVERED: "Delivered",
  CANCELLED: "Cancelled",
  REFUNDED: "Refunded",
  FORFEITED: "Forfeited",
};

/** Allowed next fulfilment status from the current order status. */
export const ORDER_STATUS_TRANSITIONS: Record<string, FulfilOrderStatus[]> = {
  PAID: ["PICKED"],
  PICKED: ["PACKED"],
  PACKED: ["SHIPPED"],
  SHIPPED: ["DELIVERED"],
};

export function orderStatusLabel(status: string): string {
  return ORDER_STATUS_LABELS[status] ?? status.replace(/_/g, " ");
}

/** Customer-facing label when bank-in receipt is awaiting staff review. */
export function customerOrderStatusLabel(
  orderStatus: string,
  latestReceiptStatus?: string | null,
): string {
  if (orderStatus === "PENDING_PAYMENT" && latestReceiptStatus === "PENDING") {
    return "Pending verification";
  }
  if (orderStatus === "PENDING_PAYMENT") {
    return "Pending payment";
  }
  return orderStatusLabel(orderStatus);
}

/** Whether the customer may still update shipping on an unpaid order. */
export function canChangeOrderShippingAddress(
  orderStatus: string,
  latestReceiptStatus?: string | null,
): boolean {
  if (orderStatus !== "PENDING_PAYMENT") return false;
  if (latestReceiptStatus === "PENDING") return false;
  return true;
}

/** Badge color key for customer order lists (maps to storefront statusColor). */
export function customerOrderStatusColorKey(
  orderStatus: string,
  latestReceiptStatus?: string | null,
): string {
  if (orderStatus === "PENDING_PAYMENT" && latestReceiptStatus === "PENDING") {
    return "PENDING";
  }
  return orderStatus;
}

export function orderProgressStepIndex(status: string): number {
  const idx = ORDER_PROGRESS_STATUSES.indexOf(status as OrderProgressStatus);
  return idx;
}

/** Progress labels for pickup orders (skip Shipped step in UI). */
export function orderProgressLabel(status: string, fulfilmentMethod?: string | null): string {
  if (fulfilmentMethod === "PICKUP" && status === "DELIVERED") return "Collected";
  if (fulfilmentMethod === "PICKUP" && status === "SHIPPED") return "Shipped";
  return orderStatusLabel(status);
}
